import { computed, inject, Injectable, resource } from '@angular/core';

import { DealerProvider } from '../../../auth/dealer/dealer.provider';
import { GcdmService } from '../clients/gcdm/gcdm.service';
import { ConsentSelection } from '../model/consent-checkbox';
import {
  NSCConsent,
  NSCConsentChoice,
  NSCConsents,
  NSCConsentSelection,
  NSCConsentUsage,
  NSCConsentVariant,
} from '../model/nsc-consent';
import { PreregistrationData } from '../model/preregistration-data';

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private readonly gcdmService = inject(GcdmService);
  private readonly dealerProvider = inject(DealerProvider);

  async getNscConsents(brand: string | undefined) {
    return await this.gcdmService.getNscConsents(brand);
  }

  private readonly getNscConsentsResource = resource({
    params: this.dealerProvider.activeCountry,
    loader: () => this.getNscConsents(/* TODO: brand? */ undefined),
  });

  readonly nscConsentData = computed(() => this.getNscConsentsResource.value());

  async getConsentSelection(gcid: string): Promise<NSCConsentSelection> {
    return await this.gcdmService.getNscConsentSelection(gcid);
  }

  private computeSelection(
    usage: NSCConsentUsage,
    consentSelected: boolean,
    anySelected: boolean,
    addImplicits: boolean,
  ) {
    if (usage.usageVariant !== NSCConsentVariant.IMPLICIT) {
      return consentSelected;
    } else if (addImplicits) {
      // If we are adding the implicits (initial consent creation), we overwrite the existing value
      return anySelected;
    } else {
      // Otherwise (modifying consents), we only unset the existing value if no other consents are selected
      return anySelected && consentSelected;
    }
  }

  async confirmConsentSelection(
    gcid: string,
    consentSelection: ConsentSelection,
    consents: NSCConsents,
    addImplicits: boolean,
  ) {
    const anySelected = ConsentService.anyNonImplicitConsentsSelected(consentSelection, consents.consents);
    const consentChoices = consents.consents.flatMap((consent) =>
      consent.usages.map(
        (usage): NSCConsentChoice => ({
          selected: this.computeSelection(usage, consentSelection[usage.name] || false, anySelected, addImplicits),
          nscConsent: consent,
          nscConsentUsage: usage,
        }),
      ),
    );
    return await this.gcdmService.setNscConsentChoices(gcid, consentChoices);
  }

  async preregister(request: PreregistrationData) {
    return await this.gcdmService.preregister(request);
  }

  static anyNonImplicitConsentsSelected(consentSelection: ConsentSelection, consents: NSCConsent[]) {
    return consents.some((consent) =>
      consent.usages.some((usage) => usage.usageVariant !== NSCConsentVariant.IMPLICIT && consentSelection[usage.name]),
    );
  }

  /**
   * Pure function to select all implicit consent of a consentSelection
   */
  static selectImplicitConsents(consentSelection?: ConsentSelection, consents?: NSCConsent[]) {
    if (!consentSelection || !consents) {
      return undefined;
    }

    // Make sure that there are no side effects of this function
    const copiedConsentSelection = { ...consentSelection };

    consents?.forEach((consent) => {
      consent?.usages.forEach((usage) => {
        if (usage.usageVariant === NSCConsentVariant.IMPLICIT) {
          copiedConsentSelection[usage.name] = true;
        }
      });
    });

    return copiedConsentSelection;
  }
}
